import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "tw-min-h-[calc(100vh-60px)] tw-bg-gradient-to-br tw-from-slate-50 tw-via-white tw-to-indigo-50 tw-w-full"
}
const _hoisted_2 = { class: "tw-w-full tw-p-[10px]" }
const _hoisted_3 = { class: "tw-relative" }
const _hoisted_4 = {
  key: 0,
  class: "tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-backdrop-blur"
}
const _hoisted_5 = { class: "tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/80 tw-px-4 tw-py-3 tw-shadow-sm" }
const _hoisted_6 = { class: "tw-text-sm tw-font-medium tw-text-slate-700" }
const _hoisted_7 = {
  key: 0,
  class: "tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-backdrop-blur"
}
const _hoisted_8 = { class: "tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/80 tw-px-4 tw-py-3 tw-shadow-sm" }
const _hoisted_9 = { class: "tw-text-sm tw-font-medium tw-text-slate-700" }

import { computed, ref, watch } from 'vue';

type ListPageVariant = 'page' | 'card';


export default /*@__PURE__*/_defineComponent({
  __name: 'ListPage',
  props: {
    variant: { default: 'page' },
    loading: { type: Boolean, default: false },
    loadingText: { default: 'Loading…' },
    cardPadding: { type: Boolean, default: true },
    enableSelection: { type: Boolean, default: false },
    selectionItems: { default: () => [] },
    selectionKey: { default: 'id' },
    pageSize: {},
    pageSizeOptions: { default: () => [10, 25, 50, 100, 250] }
  },
  emits: ["update:pageSize"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit;

const props = __props;

const normalizedPageSizeOptions = computed<number[]>(() => {
  const opts = (props.pageSizeOptions || []).filter((x) => Number.isFinite(x) && x > 0);
  if (!opts.length) return [10, 25, 50, 100, 250];
  return Array.from(new Set(opts)).sort((a, b) => a - b);
});

function setPageSize(value: number) {
  if (!Number.isFinite(value)) return;
  emit('update:pageSize', value);
}

const selectedKeys = ref<Array<string | number>>([]);

const itemKeys = computed<Array<string | number>>(() => {
  if (!props.enableSelection) return [];
  const key = props.selectionKey;
  return (props.selectionItems || [])
    .map((item: any) => item?.[key])
    .filter((k: any) => k !== undefined && k !== null);
});

watch(
  itemKeys,
  (keys) => {
    if (!props.enableSelection) {
      selectedKeys.value = [];
      return;
    }

    const keySet = new Set(keys);
    selectedKeys.value = selectedKeys.value.filter((k) => keySet.has(k));
  },
  { immediate: true },
);

function isSelected(key: string | number): boolean {
  return selectedKeys.value.includes(key);
}

function toggleKey(key: string | number, toState: null | boolean = null) {
  if (!props.enableSelection) return;
  let isNextKeySelected = toState;
  if (isNextKeySelected === null) {
    isNextKeySelected = !isSelected(key);
  }
  if (!isNextKeySelected) {
    selectedKeys.value = selectedKeys.value.filter((k) => k !== key);
  } else {
    selectedKeys.value = [...selectedKeys.value, key];
  }
}

const allSelected = computed<boolean>(() => {
  if (!props.enableSelection) return false;
  const keys = itemKeys.value;
  if (!keys.length) return false;
  return keys.every((k) => selectedKeys.value.includes(k));
});

const someSelected = computed<boolean>(() => {
  if (!props.enableSelection) return false;
  return selectedKeys.value.length > 0 && !allSelected.value;
});

function toggleAll() {
  if (!props.enableSelection) return;
  if (allSelected.value) {
    selectedKeys.value = [];
  } else {
    selectedKeys.value = [...itemKeys.value];
  }
}

function clearSelection() {
  selectedKeys.value = [];
}

const selectionApi = computed(() => {
  return {
    enabled: props.enableSelection,
    keys: selectedKeys.value,
    count: selectedKeys.value.length,
    allSelected: allSelected.value,
    someSelected: someSelected.value,
    toggleKey,
    toggleAll,
    isSelected,
    clearSelection,
  };
});

__expose({ selectionApi });

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (__props.variant === 'page')
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            (__props.loading)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_component_font_awesome_icon, {
                      icon: "circle-notch",
                      spin: "",
                      class: "tw-h-4 tw-w-4 tw-text-slate-600"
                    }),
                    _createElementVNode("span", _hoisted_6, _toDisplayString(__props.loadingText), 1)
                  ])
                ]))
              : _createCommentVNode("", true),
            _renderSlot(_ctx.$slots, "default", {
              selection: selectionApi.value,
              pageSize: props.pageSize,
              pageSizeOptions: normalizedPageSizeOptions.value,
              setPageSize: setPageSize
            })
          ])
        ])
      ]))
    : (_openBlock(), _createElementBlock("div", {
        key: 1,
        class: _normalizeClass(["tw-relative tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50", __props.cardPadding ? 'tw-p-3 sm:tw-p-4' : ''])
      }, [
        (__props.loading)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _createVNode(_component_font_awesome_icon, {
                  icon: "circle-notch",
                  spin: "",
                  class: "tw-h-4 tw-w-4 tw-text-slate-600"
                }),
                _createElementVNode("span", _hoisted_9, _toDisplayString(__props.loadingText), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        _renderSlot(_ctx.$slots, "default", {
          selection: selectionApi.value,
          pageSize: props.pageSize,
          pageSizeOptions: normalizedPageSizeOptions.value,
          setPageSize: setPageSize
        })
      ], 2))
}
}

})